import * as THREE from 'bmap-three';
import { loadGLTF } from './loader';
import { useDomLabelManager } from './label-manager';
import { isArray } from '../is';
import { arrify } from '../arrify';

/**
 * 无人机管理函数
 * @param {Object} params - 配置参数对象
 * @param {Function} params.onProgress - 加载进度回调函数
 * @param {[number,number, number]} params.offset - 偏移量
 * @param {import('mapv-three').Engine} Params.engine - 地图引擎实例
 */
export class UAVManager {
  constructor({ onProgress, engine, url, offset, rotation }) {
    this.url = url;
    this.onLoadProgress = onProgress;
    this.offset = offset || [0, 0, 0];
    this.rotation = rotation || [Math.PI * 0.5, 0, 0];
    this.engine = engine;

    /**
     * @type {Record<string, { instance: THREE.Object3D, mixer: THREE.AnimationMixer, config: { name: string, position: [number, number, number], onClick: Function } }>}
     */
    this.record = {};

    /**
     * @typedef {{ t:number, step: number, points: THREE.Vector3[], max: number, ind: number, len: number, a: THREE.Vector3, b: THREE.Vector3, data: any }} IMoveConfig
     * @type {Record<string, IMoveConfig>}
     */
    this.moveConfig = {};
    this.moveSubs = [];

    this.isMoving = true;
  }

  async init() {
    await this.loadModel();
    await this.loadLabelManager();
  }

  async loadModel() {
    const gltf = await loadGLTF({
      url: this.url,
      onProgress: this.onLoadProgress
    });

    this.gltf = gltf;
    this.model = gltf.scene;
  }

  async loadLabelManager() {
    this.labelManager = await useDomLabelManager({
      engine: this.engine,
      data: [],
      config: {
        minUpdateInterval: 1000,
        zooms: [13, 21],
        offset: [0, -60]
      }
    });
  }

  addUAV({ name, position, onClick }) {
    const { record, model, labelManager, engine, rotation } = this;

    if (record[name]) {
      return;
    }

    /** @type {THREE.Object3D} */
    const instance = model.clone();

    instance.name = name;

    labelManager.addLabel({
      id: name,
      position,
      text: name
    });

    const mixer = new THREE.AnimationMixer(instance);
    mixer.clipAction(this.gltf.animations[0]).setDuration(1).play();

    record[name] = { instance, mixer, config: { name, position, onClick } };

    const pos = engine.map.projectPointArr(position);

    instance.rotation.x = rotation[0];
    instance.rotation.y = rotation[1];
    instance.rotation.z = rotation[2];

    instance.position.set(...this.getFixPos(pos));

    engine.add(instance);
    engine.event.markEventProxy(instance);

    onClick && engine.event.bind(instance, 'click', onClick);
  }

  getFixPos(pos, type) {
    const { offset } = this;

    if (pos.isVector3) {
      if (type === 'vec3') {
        return new THREE.Vector3(pos.x + offset[0], pos.y + offset[1], pos.z + offset[2]);
      }

      return [pos.x + offset[0], pos.y + offset[1], pos.z + offset[2]];
    }

    if (type === 'vec3') {
      return new THREE.Vector3(pos[0] + offset[0], pos[1] + offset[1], pos[2] + offset[2]);
    }

    return [pos[0] + offset[0], pos[1] + offset[1], pos[2] + offset[2]];
  }

  setPosition(name, position) {
    const { record, labelManager } = this;

    if (!record[name]) return;

    // 设置模型朝向
    const instance = record[name].instance;
    instance.position.copy(this.getFixPos(position, 'vec3'));

    labelManager.upadteLabel({
      id: name,
      position: [position.x, position.y, position.z],
      text: name
    });
  }

  resetPosition(name, position) {
    const { record, labelManager, moveConfig, getFixPos } = this;

    if (!record[name]) return;

    const instance = record[name].instance;
    instance.position.copy(getFixPos(position, 'vec3'));
    labelManager.upadteLabel({
      id: name,
      position: [position.x, position.y, position.z],
      text: name
    });

    moveConfig[name] = null;
  }

  delUAV(name) {
    const { record, labelManager, engine, moveConfig } = this;

    if (!record[name]) return;

    const { instance, config } = record[name];
    const { onClick } = config;

    labelManager.removeLabel(name);

    engine.remove(instance);
    onClick && engine.event.unbind(instance, 'click', onClick);

    delete moveConfig[name];
    delete record[name];
  }

  onMove(cb) {
    this.moveSubs.push(cb);
  }

  getProjVec3(pos) {
    const arr = this.engine.map.projectPointArr(pos);
    return new THREE.Vector3(...arr);
  }

  moveTo({ name, position, data, step = 2 }) {
    const { record, moveConfig } = this;

    if (!record[name]) return;

    const posArr = [];

    const temp = isArray(position[0]) ? position : [position];

    temp.forEach(pos => {
      posArr.push(this.getProjVec3(pos));
    });

    if (!moveConfig[name]) {
      const points = [record[name].instance.position.clone(), ...posArr];
      const max = points.length;

      moveConfig[name] = {
        t: Infinity,
        step,
        ind: -1,
        max,
        name,
        data: [data],
        len: 0,
        points
      };
    } else {
      moveConfig[name].points.push(...posArr);
      moveConfig[name].data.push(data);
      moveConfig[name].max = moveConfig[name].points.length;
    }
  }

  insertBefore(name, data) {
    const { moveConfig } = this;

    data = arrify(data);

    let cfg = moveConfig[name];

    const arr = data.map(e => this.getProjVec3(e.position));

    if (!cfg) {
      const max = arr.length;

      moveConfig[name] = {
        t: Infinity,
        step: 2,
        ind: data.length - 1,
        max,
        name,
        data: data,
        len: 0,
        points: arr
      };
    } else {
      cfg.points.unshift(...arr);
      cfg.data.unshift(...data);
      cfg.ind = cfg.ind + arr.length;
      cfg.max = moveConfig[name].points.length;
    }
  }

  resetData({ name, data, step }) {
    const { record, moveConfig } = this;

    if (!record[name]) return;

    const points = [
      record[name].instance.position.clone(),
      ...data.map(e => this.getProjVec3(e.position))
    ];

    const lastPos = points[points.length - 1];

    this.setPosition(name, lastPos);

    step = step || moveConfig[name]?.step || 2;

    moveConfig[name] = {
      t: Infinity,
      step,
      ind: points.length - 1,
      max: points.length,
      name,
      data: [...data],
      len: 0,
      points
    };

    this.moveSubs.forEach(cb => cb(moveConfig[name]));
  }

  updateMoveConfig(item) {
    if (item.points.length < 2) return;

    // 跳转到下一段路径
    if (item.t >= item.len && item.max - 2 > item.ind) {
      item.ind += 1;
      item.t = 0;

      item.a = item.points[item.ind].clone();
      item.b = item.points[item.ind + 1].clone();

      // 计算模型朝向
      const dir = item.b.clone().sub(item.a).normalize();
      const angle = Math.atan2(dir.y, dir.x);

      this.record[item.name].instance.rotation.y = angle + Math.PI / 2;

      item.len = item.a.distanceTo(item.b);
    }

    // 当前路段移动
    if (item.max - 1 > item.ind && item.t < item.len) {
      item.t += item.step;

      const r = Math.min(1, item.t / item.len);

      const p = item.a.clone().lerp(item.b, r);

      if (r === 1) {
        this.moveSubs.forEach(cb => cb(item));
      }

      this.setPosition(item.name, p);
    }
  }

  enableMove(val) {
    this.isMoving = val;
  }

  update = (t, name) => {
    try {
      const { record, moveConfig } = this;

      if (this.isMoving) {
        Object.values(moveConfig).forEach(item => {
          this.updateMoveConfig(item);
        });
      }

      if (name) {
        record[name] && record[name]?.mixer?.update(t);
      } else {
        Object.values(record).forEach(item => {
          item.mixer?.update?.(t);
        });
      }
    } catch (error) {
      console.log('更新无人机动画失败', error);
    }
  };

  dispose() {
    this.labelManager.dispose();

    Object.values(this.record).forEach(item => {
      this.engine.remove(item.instance);
      item.instance.dispose();
    });

    this.model.dispose();
  }
}
